import React from "react";
import ReactDOM from "react-dom";
import clsx from "clsx";
import { ClassTaxonomyNode } from "@semantical/modules/ontology-model";
import type { PrefixMap } from "@semantical/modules/ontology-model";
import { NamespaceTag } from './NamespaceTag';
import type { Workspace } from '@semantical/modules/workspace';

type Props = {
    taxonomy: ClassTaxonomyNode[];
    onClassSelected: (node: ClassTaxonomyNode) => void;
    onClassHovered?: (node: ClassTaxonomyNode | null) => void;
    darkMode?: boolean;
    domainPrefixes?: PrefixMap;
    getNamespaceColor?: (namespace: string) => string | undefined;
    persistenceService?: any;  // For saving view mode preferences
    boundedContext?: Workspace;  // For accessing ontology metadata
    activeDomainNs?: string;  // Active domain namespace for sorting
};

/**
 * Recursively check if all nodes in the taxonomy have icons
 */
function allNodesHaveIcons(nodes: ClassTaxonomyNode[]): boolean {
    for (const node of nodes) {
        if (!node.icon) {
            return false;
        }
        if (node.children && node.children.length > 0) {
            if (!allNodesHaveIcons(node.children)) {
                return false;
            }
        }
    }
    return true;
}


type State = {
    expanded: boolean;
    menuStack: Array<{
        node: ClassTaxonomyNode;
        rect: DOMRect;
    }>;
    hoveredId: string | null;
    viewMode: 'hierarchical' | 'flat';
    groupByOntology: boolean;
    collapsedGroups: Set<string>;  // Set of collapsed group prefixes
};

// Portal component for rendering menus outside the container
class MenuPortal extends React.Component<{ children: React.ReactNode }> {
    private el: HTMLDivElement;

    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.el = document.createElement("div");
        this.el.className = "fixed top-0 left-0 z-[10000] pointer-events-none";
    }

    componentDidMount() {
        document.body.appendChild(this.el);
    }

    componentWillUnmount() {
        document.body.removeChild(this.el);
    }

    render() {
        return ReactDOM.createPortal(this.props.children, this.el);
    }
}

// Floating menu panel
type MenuPanelProps = {
    nodes: ClassTaxonomyNode[];
    anchorRect: DOMRect;
    level: number;
    hoveredId: string | null;
    onHover: (id: string | null, rect: DOMRect | null, node: ClassTaxonomyNode | null) => void;
    onSelect: (node: ClassTaxonomyNode) => void;
    darkMode?: boolean;
    domainPrefixes?: PrefixMap;
    getNamespaceColor?: (namespace: string) => string | undefined;
    getNamespaceFromPrefix?: (prefix: string) => string;
};

class MenuPanel extends React.Component<MenuPanelProps, { adjustedTop: number | null }> {
    private itemRefs = new Map<string, HTMLDivElement>();
    private menuRef = React.createRef<HTMLDivElement>();

    state = {
        adjustedTop: null as number | null,
    };

    componentDidMount() {
        this.adjustPosition();
    }

    componentDidUpdate(prevProps: MenuPanelProps) {
        // Re-adjust if anchor changed
        if (prevProps.anchorRect !== this.props.anchorRect) {
            this.adjustPosition();
        }
    }

    /**
     * Adjust menu position to keep it within viewport bounds.
     * Called after mount to measure actual menu height.
     */
    private adjustPosition() {
        const menu = this.menuRef.current;
        if (!menu) return;

        const { anchorRect } = this.props;
        const menuHeight = menu.offsetHeight;
        const viewportHeight = window.innerHeight;
        const padding = 8; // Min distance from viewport edges

        // Start aligned with the anchor item
        let top = anchorRect.top;

        // If menu would overflow bottom, shift it up
        if (top + menuHeight > viewportHeight - padding) {
            top = viewportHeight - menuHeight - padding;
        }

        // Ensure menu doesn't go above viewport
        top = Math.max(padding, top);

        this.setState({ adjustedTop: top });
    }

    render() {
        const { nodes, anchorRect, hoveredId, onHover, onSelect } = this.props;
        const { adjustedTop } = this.state;

        // Position menu to the right of anchor
        const left = anchorRect.right + 4;
        // Use adjusted position if available, otherwise start at anchor top
        const top = adjustedTop !== null ? adjustedTop : anchorRect.top;

        return (
            <div
                ref={this.menuRef}
                className="fixed w-fit min-w-40 max-h-[calc(100vh-40px)] overflow-y-auto bg-white/[0.98] dark:bg-[rgba(28,30,36,0.98)] rounded-xl shadow-[0_12px_48px_rgba(100,120,140,0.2),0_0_0_1px_rgba(226,232,240,1)] dark:shadow-[0_12px_48px_rgba(0,0,0,0.6),0_0_0_1px_rgba(100,255,180,0.15)] py-2 pointer-events-auto backdrop-blur-[16px] animate-menu-slide-in text-slate-700 dark:text-white/85"
                style={{ left, top }}
            >
                {/* All nodes are selectable classes - some may also have children */}
                {nodes.map(node => {
                    const isHovered = hoveredId === node.id;
                    const hasIcon = !!node.icon;
                    const hasChildren = node.children && node.children.length > 0;

                    return (
                        <div
                            key={node.uri}
                            ref={(el) => { if (el) this.itemRefs.set(node.id, el); }}
                            className={clsx(
                                "flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-all duration-100",
                                isHovered && "bg-accent-teal/10 dark:bg-accent/[0.18]"
                            )}
                            onMouseEnter={() => {
                                const el = this.itemRefs.get(node.id);
                                const rect = el?.getBoundingClientRect() || null;
                                // Always pass the node for hover info; pass rect only if it has children (for submenu)
                                onHover(node.id, hasChildren ? rect : null, node);
                            }}
                            onClick={() => onSelect(node)}
                        >
                            {hasIcon && (
                                <div
                                    className={clsx(
                                        "w-7 h-7 flex items-center justify-center rounded-md flex-shrink-0",
                                        node.isImported
                                            ? "bg-violet-500/10 dark:bg-imported/20 text-violet-600 dark:text-imported"
                                            : "bg-accent-teal/10 dark:bg-accent/20 text-accent-teal dark:text-accent"
                                    )}
                                    style={{
                                        backgroundColor: node.categoryColor || (
                                            node.isImported && node.namespacePrefix && this.props.getNamespaceFromPrefix
                                                ? this.props.getNamespaceColor?.(this.props.getNamespaceFromPrefix(node.namespacePrefix))
                                                : undefined
                                        ) || (node.isImported ? 'rgba(139,92,246,0.08)' : undefined)
                                    }}
                                >
                                    <svg width="14" height="14" viewBox="0 0 12 12">
                                        {node.icon}
                                    </svg>
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-medium">
                                    {node.label}
                                    {node.namespacePrefix && this.props.getNamespaceFromPrefix && (
                                        <NamespaceTag
                                            prefix={node.namespacePrefix}
                                            color={this.props.getNamespaceColor?.(this.props.getNamespaceFromPrefix(node.namespacePrefix))}
                                            darkMode={this.props.darkMode}
                                            readOnly
                                        />
                                    )}
                                </div>
                                {node.stereotype && (
                                    <div className="text-[11px] text-accent-teal/65 dark:text-accent/65 italic mt-0.5">
                                        &laquo;{node.stereotype}&raquo;
                                    </div>
                                )}
                            </div>
                            {/* Show chevron if has children (can expand to see subclasses) */}
                            {hasChildren && (
                                <div className="opacity-40 ml-1">
                                    <svg width="12" height="12" viewBox="0 0 12 12">
                                        <path d="M4,2 L8,6 L4,10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                    </svg>
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Empty state */}
                {nodes.length === 0 && (
                    <div className="py-5 px-4 text-center text-slate-400 dark:text-white/40 text-xs">No items</div>
                )}
            </div>
        );
    }

}

export default class Toolbox extends React.Component<Props, State> {
    private containerRef = React.createRef<HTMLDivElement>();
    private itemRefs = new Map<string, HTMLDivElement>();
    private closeTimeout: ReturnType<typeof setTimeout> | null = null;

    state: State = {
        expanded: false,
        menuStack: [],
        hoveredId: null,
        viewMode: 'hierarchical',
        groupByOntology: false,
        collapsedGroups: new Set(),
    };

    componentDidMount() {
        // Load saved view preferences from localStorage
        if (this.props.persistenceService) {
            const savedMode = this.props.persistenceService.loadToolboxViewMode();
            const savedGrouping = this.props.persistenceService.loadToolboxGroupByOntology();
            this.setState({
                viewMode: savedMode,
                groupByOntology: savedGrouping,
            });
        }
    }

    componentWillUnmount() {
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
        }
    }

    handleToggleExpand = () => {
        this.setState(s => ({ expanded: !s.expanded, menuStack: [] }));
    };

    handleCloseAllMenus = () => {
        // Delay closing to allow moving to submenus
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
        }
        this.closeTimeout = setTimeout(() => {
            this.setState({ menuStack: [], hoveredId: null });
            // Clear hovered class in parent
            this.props.onClassHovered?.(null);
        }, 150);
    };

    handleCancelClose = () => {
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
            this.closeTimeout = null;
        }
    };

    handleMainItemHover = (node: ClassTaxonomyNode) => {
        this.handleCancelClose();

        const el = this.itemRefs.get(node.id);
        const rect = el?.getBoundingClientRect();

        // Notify parent of hovered class
        this.props.onClassHovered?.(node);

        if (rect && node.children && node.children.length > 0) {
            this.setState({
                hoveredId: node.id,
                menuStack: [{ node, rect }],
            });
        } else {
            this.setState({
                hoveredId: node.id,
                menuStack: [],
            });
        }
    };

    handleMenuItemHover = (level: number) => (id: string | null, rect: DOMRect | null, node: ClassTaxonomyNode | null) => {
        this.handleCancelClose();

        // Notify parent of hovered class (node is now always passed from MenuPanel)
        this.props.onClassHovered?.(node);

        if (node && rect && node.children && node.children.length > 0) {
            // Open submenu for this category
            this.setState(s => ({
                hoveredId: id,
                menuStack: [
                    ...s.menuStack.slice(0, level),
                    { node, rect },
                ],
            }));
        } else {
            // It's a leaf or no rect, just update hover and trim stack
            this.setState(s => ({
                hoveredId: id,
                menuStack: s.menuStack.slice(0, level),
            }));
        }
    };

    handleSelect = (node: ClassTaxonomyNode) => {
        this.props.onClassSelected(node);
        this.setState({ menuStack: [], hoveredId: null });
        // Clear hovered class in parent after selection
        this.props.onClassHovered?.(null);
    };

    getNamespaceFromPrefix = (prefix: string): string => {
        // Look up namespace URI from prefix using domainPrefixes
        for (const [ns, pre] of this.props.domainPrefixes?.entries() || []) {
            if (pre === prefix) return ns;
        }
        return '';
    };

    /**
     * Toggle between hierarchical and flat view modes
     */
    handleToggleViewMode = () => {
        this.setState(
            s => ({
                viewMode: s.viewMode === 'hierarchical' ? 'flat' : 'hierarchical',
                menuStack: [],  // Clear menu stack when switching views
                hoveredId: null,
            }),
            () => {
                // Save to localStorage
                this.props.persistenceService?.saveToolboxViewMode(this.state.viewMode);
            }
        );
    };

    /**
     * Toggle grouping by ontology (flat mode only)
     */
    handleToggleGroupByOntology = () => {
        this.setState(
            s => ({ groupByOntology: !s.groupByOntology }),
            () => {
                // Save to localStorage
                this.props.persistenceService?.saveToolboxGroupByOntology(this.state.groupByOntology);
            }
        );
    };

    /**
     * Toggle collapse state of a group in grouped flat view
     */
    handleToggleGroupCollapse = (prefix: string) => {
        this.setState(s => {
            const newCollapsed = new Set(s.collapsedGroups);
            if (newCollapsed.has(prefix)) {
                newCollapsed.delete(prefix);
            } else {
                newCollapsed.add(prefix);
            }
            return { collapsedGroups: newCollapsed };
        });
    };

    /**
     * Toggle collapse/expand all groups in grouped flat view
     */
    handleToggleCollapseAllGroups = () => {
        const flatClasses = this.flattenTaxonomy(this.props.taxonomy);
        const groups = this.groupByOntologyPrefix(flatClasses);
        const allPrefixes = Array.from(groups.keys());

        // If all groups are collapsed, expand all; otherwise collapse all
        const allCollapsed = allPrefixes.every(prefix => this.state.collapsedGroups.has(prefix));

        if (allCollapsed) {
            // Expand all
            this.setState({ collapsedGroups: new Set() });
        } else {
            // Collapse all
            this.setState({ collapsedGroups: new Set(allPrefixes) });
        }
    };

    /**
     * Check if all groups are currently collapsed
     */
    areAllGroupsCollapsed = (): boolean => {
        const flatClasses = this.flattenTaxonomy(this.props.taxonomy);
        const groups = this.groupByOntologyPrefix(flatClasses);
        const allPrefixes = Array.from(groups.keys());
        return allPrefixes.length > 0 && allPrefixes.every(prefix => this.state.collapsedGroups.has(prefix));
    };

    /**
     * Get ontology label for a namespace.
     * Uses Workspace.getOntologyLabel() to retrieve the English label.
     */
    getOntologyLabel = (prefix: string): string => {
        // For "Local", return as-is
        if (prefix === 'Local') {
            return 'Local';
        }

        // Get namespace from prefix
        const namespace = this.getNamespaceFromPrefix(prefix);
        if (!namespace || !this.props.boundedContext) {
            return prefix;
        }

        // Use BoundedContext API to get the ontology label with English preference
        const label = this.props.boundedContext.getOntologyLabel(namespace, 'en');

        // Fall back to prefix if no label found
        return label || prefix;
    };

    /**
     * Flatten taxonomy tree to get all classes (both leaf and parent nodes)
     */
    private flattenTaxonomy(nodes: ClassTaxonomyNode[]): ClassTaxonomyNode[] {
        const seen = new Set<string>();
        const result: ClassTaxonomyNode[] = [];

        const traverse = (nodeList: ClassTaxonomyNode[]) => {
            for (const node of nodeList) {
                // Deduplicate by URI
                if (!seen.has(node.uri)) {
                    seen.add(node.uri);
                    result.push(node);
                }

                // Recursively process children
                if (node.children && node.children.length > 0) {
                    traverse(node.children);
                }
            }
        };

        traverse(nodes);

        // Sort alphabetically by label
        return result.sort((a, b) => a.label.localeCompare(b.label));
    }

    /**
     * Group classes by namespace/ontology
     * Returns a map of prefix -> classes array
     */
    private groupByOntologyPrefix(
        nodes: ClassTaxonomyNode[]
    ): Map<string, ClassTaxonomyNode[]> {
        const groups = new Map<string, ClassTaxonomyNode[]>();

        for (const node of nodes) {
            const prefix = node.namespacePrefix || 'Local';

            if (!groups.has(prefix)) {
                groups.set(prefix, []);
            }
            groups.get(prefix)!.push(node);
        }

        // Sort classes within each group alphabetically
        for (const [, classes] of groups) {
            classes.sort((a, b) => a.label.localeCompare(b.label));
        }

        return groups;
    }

    /**
     * Render a single class item in flat view mode
     */
    private renderFlatClassItem(node: ClassTaxonomyNode, isExpanded: boolean): React.ReactElement {
        const { hoveredId } = this.state;
        const isHovered = hoveredId === node.id;
        const hasIcon = !!node.icon;

        return (
            <div
                key={node.uri}
                className={clsx(
                    "flex items-center gap-2.5 px-2.5 py-1.5 mx-1.5 rounded-[10px] cursor-pointer transition-all duration-[120ms]",
                    isHovered && "bg-accent-teal/[0.08] dark:bg-accent/10"
                )}
                onMouseEnter={() => this.setState({ hoveredId: node.id })}
                onMouseLeave={() => this.setState({ hoveredId: null })}
                onClick={() => this.handleSelect(node)}
                title={!isExpanded ? node.label : undefined}
            >
                {hasIcon && (
                    <div
                        className={clsx(
                            "relative w-[38px] h-[38px] flex items-center justify-center rounded-[10px] flex-shrink-0",
                            node.isImported
                                ? "bg-violet-500/10 dark:bg-imported/20 text-violet-600 dark:text-imported"
                                : "bg-accent-teal/10 dark:bg-accent/20 text-accent-teal dark:text-accent"
                        )}
                        style={{
                            backgroundColor: node.categoryColor || (
                                node.isImported && node.namespacePrefix
                                    ? this.props.getNamespaceColor?.(this.getNamespaceFromPrefix(node.namespacePrefix))
                                    : undefined
                            ) || (node.isImported ? 'rgba(139,92,246,0.08)' : undefined)
                        }}
                    >
                        <svg width="20" height="20" viewBox="0 0 12 12">
                            {node.icon}
                        </svg>
                    </div>
                )}
                {isExpanded && (
                    <div className="flex-1 text-xs font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                        {node.label}
                        {node.namespacePrefix && (
                            <NamespaceTag
                                prefix={node.namespacePrefix}
                                color={this.props.getNamespaceColor?.(this.getNamespaceFromPrefix(node.namespacePrefix))}
                                darkMode={this.props.darkMode}
                                readOnly
                            />
                        )}
                    </div>
                )}
            </div>
        );
    }

    render() {
        const { taxonomy, darkMode } = this.props;
        const { expanded, menuStack, hoveredId } = this.state;

        // Check if all nodes have icons - if not, force expanded mode
        const hasAllIcons = allNodesHaveIcons(taxonomy);
        const isExpanded = hasAllIcons ? expanded : true;
        const canToggle = hasAllIcons;

        const collapsedWidth = 66;
        const hasOpenMenu = menuStack.length > 0;

        return (
            <>
                <div
                    ref={this.containerRef}
                    className="flex flex-col h-full bg-white/[0.98] dark:bg-surface-dark border-r border-slate-200 dark:border-accent-border font-sans text-[13px] text-slate-700 dark:text-[#e0e0e0] transition-[width] duration-200 overflow-visible"
                    style={{ width: isExpanded ? 'fit-content' : collapsedWidth, minWidth: isExpanded ? 160 : collapsedWidth }}
                    onMouseLeave={this.handleCloseAllMenus}
                    onMouseEnter={this.handleCancelClose}
                >
                    {/* Header with title */}
                    <div className="flex items-center gap-2.5 px-2.5 py-3 border-b border-slate-200 dark:border-white/[0.06]">
                        {canToggle ? (
                            <button
                                className="flex items-center justify-center w-[38px] h-[38px] p-0 bg-accent-teal/10 dark:bg-accent/10 border border-accent-teal/25 dark:border-accent/25 rounded-[10px] cursor-pointer text-accent-teal dark:text-accent transition-all duration-150 hover:bg-accent-teal/20 dark:hover:bg-accent/20"
                                onClick={this.handleToggleExpand}
                                title={isExpanded ? "Collapse toolbox" : "Expand toolbox"}
                            >
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                    {isExpanded ? (
                                        <path d="M11,4 L6,9 L11,14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    ) : (
                                        <path d="M7,4 L12,9 L7,14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    )}
                                </svg>
                            </button>
                        ) : null}
                        {isExpanded && (
                            <span className="text-sm font-semibold text-slate-900 dark:text-white whitespace-nowrap">Classes</span>
                        )}
                    </div>

                    {/* View mode toggle buttons - only show when expanded */}
                    {isExpanded && (
                        <div className="flex items-center gap-1.5 px-2.5 py-2 border-b border-slate-200 dark:border-white/[0.06]">
                            {/* View mode toggle button */}
                            <button
                                className={clsx(
                                    "w-7 h-7 rounded-[5px] cursor-pointer transition-all duration-150 border flex items-center justify-center",
                                    this.state.viewMode === 'hierarchical'
                                        ? "bg-slate-100 dark:bg-white/[0.06] text-slate-400 dark:text-white/50 border-slate-200 dark:border-transparent"
                                        : "bg-accent-teal-light dark:bg-accent-muted text-teal-700 dark:text-accent/90 border-accent-teal dark:border-transparent"
                                )}
                                onClick={this.handleToggleViewMode}
                                title={this.state.viewMode === 'hierarchical' ? "Switch to flat list" : "Switch to hierarchical tree"}
                            >
                                {this.state.viewMode === 'hierarchical' ? (
                                    // List icon (shows what you'll switch TO)
                                    <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
                                        <line x1="4" y1="5" x2="14" y2="5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        <line x1="4" y1="9" x2="14" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        <line x1="4" y1="13" x2="14" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                    </svg>
                                ) : (
                                    // Tree icon (shows what you'll switch TO)
                                    <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
                                        <path d="M3,3 L3,15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        <path d="M3,6 L7,6 M3,10 L7,10 M3,14 L7,14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        <circle cx="9" cy="6" r="1.5" fill="currentColor"/>
                                        <circle cx="9" cy="10" r="1.5" fill="currentColor"/>
                                        <circle cx="9" cy="14" r="1.5" fill="currentColor"/>
                                        <path d="M7,6 L7,14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                    </svg>
                                )}
                            </button>

                            {/* Group by ontology toggle button (only in flat mode) */}
                            {this.state.viewMode === 'flat' && (
                                <button
                                    className={clsx(
                                        "w-7 h-7 rounded-[5px] cursor-pointer transition-all duration-150 border flex items-center justify-center",
                                        this.state.groupByOntology
                                            ? "bg-accent-teal-light dark:bg-accent-muted text-teal-700 dark:text-accent/90 border-accent-teal dark:border-transparent"
                                            : "bg-slate-100 dark:bg-white/[0.06] text-slate-400 dark:text-white/50 border-slate-200 dark:border-transparent"
                                    )}
                                    onClick={this.handleToggleGroupByOntology}
                                    title={this.state.groupByOntology ? "Ungroup classes" : "Group by ontology"}
                                >
                                    {/* Group/Folder icon */}
                                    <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
                                        <path d="M2,6 L2,15 L16,15 L16,6 L2,6 Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M2,6 L2,4 L8,4 L9,6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <line x1="5" y1="10" x2="13" y2="10" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
                                        <line x1="5" y1="12" x2="10" y2="12" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
                                    </svg>
                                </button>
                            )}

                            {/* Collapse/Expand all groups button (only in flat mode with grouping enabled) */}
                            {this.state.viewMode === 'flat' && this.state.groupByOntology && (
                                <button
                                    className={clsx(
                                        "w-7 h-7 rounded-[5px] cursor-pointer transition-all duration-150 border flex items-center justify-center",
                                        "bg-slate-100 dark:bg-white/[0.06] text-slate-400 dark:text-white/50 border-slate-200 dark:border-transparent",
                                        "hover:bg-slate-200 dark:hover:bg-white/[0.1]"
                                    )}
                                    onClick={this.handleToggleCollapseAllGroups}
                                    title={this.areAllGroupsCollapsed() ? "Expand all groups" : "Collapse all groups"}
                                >
                                    {this.areAllGroupsCollapsed() ? (
                                        // Expand all icon (chevrons pointing up)
                                        <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
                                            <path d="M4,12 L9,8 L14,12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M4,8 L9,4 L14,8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        // Collapse all icon (chevrons pointing down)
                                        <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
                                            <path d="M4,6 L9,10 L14,6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M4,10 L9,14 L14,10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Main toolbar items */}
                    <div className="flex-1 overflow-y-auto overflow-x-visible py-2">
                        {this.state.viewMode === 'hierarchical' ? (
                            /* ========== HIERARCHICAL VIEW ========== */
                            taxonomy.map(node => {
                                const isOpen = menuStack.length > 0 && menuStack[0].node.id === node.id;
                                const isHovered = hoveredId === node.id;
                                const hasChildren = node.children && node.children.length > 0;
                                const hasIcon = !!node.icon;

                                return (
                                    <div
                                        key={node.uri}
                                        ref={(el) => { if (el) this.itemRefs.set(node.id, el); }}
                                        className={clsx(
                                            "flex items-center gap-2.5 px-2.5 py-1.5 mx-1.5 rounded-[10px] cursor-pointer transition-all duration-[120ms]",
                                            (isHovered || isOpen) && "bg-accent-teal/[0.08] dark:bg-accent/10",
                                            isOpen && "bg-accent-teal/15 dark:bg-accent/[0.18]"
                                        )}
                                        onMouseEnter={() => this.handleMainItemHover(node)}
                                        onClick={() => {
                                            // If it's a leaf node (has classType), select it
                                            if (node.classType) {
                                                this.handleSelect(node);
                                            }
                                        }}
                                        title={!isExpanded ? node.label : undefined}
                                    >
                                        {hasIcon && (
                                            <div
                                                className={clsx(
                                                    "relative w-[38px] h-[38px] flex items-center justify-center rounded-[10px] text-white/85 transition-all duration-[120ms] flex-shrink-0",
                                                    isOpen && "shadow-[0_0_12px_rgba(59,130,152,0.2)] dark:shadow-[0_0_12px_rgba(100,255,180,0.3)]"
                                                )}
                                                style={{
                                                    backgroundColor: node.categoryColor || (node.isImported ? 'rgba(139,92,246,0.08)' : undefined),
                                                    ...(isOpen ? { backgroundColor: 'rgba(59,130,152,0.15)' } : {}),
                                                }}
                                            >
                                                <svg width="20" height="20" viewBox="0 0 12 12" className={clsx(isOpen && "text-accent-teal dark:text-accent")}>
                                                    {node.icon}
                                                </svg>
                                                {/* Small chevron indicator when collapsed */}
                                                {!isExpanded && hasChildren && (
                                                    <div className="absolute -right-0.5 -bottom-0.5 w-3.5 h-3.5 flex items-center justify-center bg-accent-teal/15 dark:bg-accent/30 rounded text-accent-teal dark:text-accent">
                                                        <svg width="8" height="8" viewBox="0 0 8 8">
                                                            <path d="M2,1 L6,4 L2,7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {isExpanded && (
                                            <>
                                                <div className="flex-1 text-xs font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                                                    {node.label}
                                                    {node.namespacePrefix && (
                                                        <NamespaceTag
                                                            prefix={node.namespacePrefix}
                                                            color={this.props.getNamespaceColor?.(this.getNamespaceFromPrefix(node.namespacePrefix))}
                                                            darkMode={this.props.darkMode}
                                                            readOnly
                                                        />
                                                    )}
                                                </div>
                                                {hasChildren && (
                                                    <div className={clsx(
                                                        "opacity-40 flex-shrink-0 transition-all duration-[120ms]",
                                                        isOpen && "opacity-100 text-accent-teal dark:text-accent"
                                                    )}>
                                                        <svg width="12" height="12" viewBox="0 0 12 12">
                                                            <path d="M4,2 L8,6 L4,10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                );
                            })
                        ) : this.state.groupByOntology ? (
                            /* ========== FLAT VIEW - GROUPED BY ONTOLOGY ========== */
                            (() => {
                                const flatClasses = this.flattenTaxonomy(taxonomy);
                                const groups = this.groupByOntologyPrefix(flatClasses);

                                // Sort groups: active domain first, then alphabetically, "Local" last
                                const sortedGroups = Array.from(groups.entries()).sort(([a], [b]) => {
                                    // Active domain first (if available)
                                    if (this.props.activeDomainNs) {
                                        const aNs = this.getNamespaceFromPrefix(a);
                                        const bNs = this.getNamespaceFromPrefix(b);
                                        if (aNs === this.props.activeDomainNs) return -1;
                                        if (bNs === this.props.activeDomainNs) return 1;
                                    }
                                    // "Local" always last
                                    if (a === 'Local') return 1;
                                    if (b === 'Local') return -1;
                                    // Otherwise alphabetically
                                    return a.localeCompare(b);
                                });

                                return sortedGroups.map(([prefix, classes]) => {
                                    const isCollapsed = this.state.collapsedGroups.has(prefix);
                                    const ontologyLabel = this.getOntologyLabel(prefix);

                                    return (
                                        <div key={prefix} className="mb-4">
                                            {/* Group Header */}
                                            <div
                                                className={clsx(
                                                    "px-2.5 mb-1.5 flex items-center gap-2 cursor-pointer transition-all duration-150",
                                                    isExpanded && "hover:bg-slate-100 dark:hover:bg-white/[0.04] rounded-md py-1"
                                                )}
                                                onClick={() => this.handleToggleGroupCollapse(prefix)}
                                                title={isCollapsed ? "Click to expand" : "Click to collapse"}
                                            >
                                                {/* Chevron icon */}
                                                {isExpanded && (
                                                    <svg
                                                        width="12"
                                                        height="12"
                                                        viewBox="0 0 12 12"
                                                        className={clsx(
                                                            "flex-shrink-0 transition-transform duration-150 text-slate-400 dark:text-white/40",
                                                            isCollapsed ? "rotate-0" : "rotate-90"
                                                        )}
                                                    >
                                                        <path d="M4,2 L8,6 L4,10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                    </svg>
                                                )}
                                                {isExpanded && (
                                                    <div className="text-[11px] font-semibold text-slate-700 dark:text-white/75">
                                                        {ontologyLabel}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Classes in Group */}
                                            {!isCollapsed && classes.map(node => this.renderFlatClassItem(node, isExpanded))}
                                        </div>
                                    );
                                });
                            })()
                        ) : (
                            /* ========== FLAT VIEW - UNGROUPED ========== */
                            this.flattenTaxonomy(taxonomy).map(node =>
                                this.renderFlatClassItem(node, isExpanded)
                            )
                        )}
                    </div>

                    {/* Footer hint */}
                    {isExpanded && (
                        <div className="py-2.5 px-3 border-t border-slate-200 dark:border-white/[0.06] text-center">
                            <span className="text-[10px] text-slate-400 dark:text-white/35">Hover to browse</span>
                        </div>
                    )}
                </div>

                {/* Render menu panels through portal - only in hierarchical mode */}
                {hasOpenMenu && this.state.viewMode === 'hierarchical' && (
                    <MenuPortal>
                        <div
                            onMouseEnter={this.handleCancelClose}
                            onMouseLeave={this.handleCloseAllMenus}
                            className="pointer-events-auto"
                        >
                            {menuStack.map((item, index) => (
                                <MenuPanel
                                    key={`${item.node.id}-${index}`}
                                    nodes={item.node.children || []}
                                    anchorRect={item.rect}
                                    level={index + 1}
                                    hoveredId={hoveredId}
                                    onHover={this.handleMenuItemHover(index + 1)}
                                    onSelect={this.handleSelect}
                                    darkMode={darkMode}
                                    domainPrefixes={this.props.domainPrefixes}
                                    getNamespaceColor={this.props.getNamespaceColor}
                                    getNamespaceFromPrefix={this.getNamespaceFromPrefix}
                                />
                            ))}
                        </div>
                    </MenuPortal>
                )}
            </>
        );
    }
}

export { Toolbox };
